import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useKothCurrent, useStats } from "@/lib/hooks/useApi";
import { usePrivy } from "@privy-io/react-auth";
import { KOTHLive } from "./-components/KOTHLive";
import { KOTHWaiting } from "./-components/KOTHWaiting";
import { KOTHEnded } from "./-components/KOTHEnded";
import { useMusic } from "@/lib/hooks/useMusic";
import { MATCH_TRACK, MENU_TRACK } from "@/lib/configs/music";
import configs from "@/lib/configs";
import type { KothCurrentResponse } from "@/lib/types/api";
import { getSyncedNow, updateClockOffset } from "@/lib/clockSync";

import AnimatedLoading from "@/components/AnimatedLoading";
import AnimatedWaiting from "@/components/AnimatedWaiting";
import { generateTextShadow } from "@/lib/utils";
import { Activity } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSetAtom } from "jotai";
import { isGameLiveAtom, kothTourModeAtom } from "@/lib/gameStatusAtom";
import { useNextStep } from "@/components/NextStepProvider";

export const Route = createFileRoute("/(game)/koth/")({
  component: RouteComponent,
});

function RouteComponent() {
  const queryClient = useQueryClient();
  const [data, setData] = useState<KothCurrentResponse | null | undefined>(
    undefined,
  );
  const [isSseErrorOpen, setIsSseErrorOpen] = useState(false);
  const hasSnapshotRef = useRef(false);

  const { user } = usePrivy();
  const walletAddress = user?.wallet?.address;
  const { data: statsData } = useStats(walletAddress ?? "");
  const gameWalletAddress = statsData?.embeddedWallet;
  const currentUserAddress = gameWalletAddress;
  const userTier = statsData?.tier != null ? Number(statsData.tier) : undefined;
  const rookieBlocked = userTier === 0;
  const kothType = 1;

  const { data: initialKoth, isPending: isKothQueryPending } = useKothCurrent({
    refetchInterval: false,
    kothType,
  });

  // SSE: real-time updates from /sse/koth
  useEffect(() => {
    let isActive = true;
    const eventSource = new EventSource(
      `${configs.API_BASE_URL}/sse/koth?kothType=${kothType}`,
    );

    const syncClock = (obj: Record<string, unknown> | null) => {
      if (obj && typeof obj.serverTime === "number") {
        updateClockOffset(obj.serverTime);
      }
    };

    const handleSnapshot = (event: Event) => {
      if (!isActive) return;
      try {
        const messageEvent = event as MessageEvent<string>;
        const payload = JSON.parse(
          messageEvent.data,
        ) as (KothCurrentResponse & { serverTime?: number }) | null;
        syncClock(payload as Record<string, unknown> | null);
        setData(payload ?? null);
        setIsSseErrorOpen(false);
        hasSnapshotRef.current = true;
      } catch (error) {
        console.error("Failed to parse KOTH snapshot:", error);
        setIsSseErrorOpen(true);
      }
    };

    const handleMessage = (event: MessageEvent<string>) => {
      if (!isActive) return;
      try {
        const payload = JSON.parse(event.data) as Record<
          string,
          unknown
        > | null;
        syncClock(payload);
        if (
          payload === null ||
          (payload && typeof payload === "object" && "kothId" in payload)
        ) {
          handleSnapshot(event as Event);
        }
      } catch (error) {
        console.error("Failed to parse KOTH message:", error);
      }
    };

    const handleStreamError = () => {
      if (!isActive) return;
      setIsSseErrorOpen(true);
    };

    eventSource.addEventListener("snapshot", handleSnapshot);
    eventSource.addEventListener("message", handleMessage);
    eventSource.addEventListener("error", handleStreamError);
    eventSource.onerror = handleStreamError;

    return () => {
      isActive = false;
      eventSource.close();
    };
  }, [kothType]);

  // Fallback: seed from initial API response when no snapshot yet
  useEffect(() => {
    if (hasSnapshotRef.current || !initialKoth) return;
    setData(initialKoth);
  }, [initialKoth]);

  const kothCurrent = data !== undefined ? data : initialKoth;

  // Derive current status and data
  const status = useMemo(() => {
    if (!kothCurrent) return "waiting";
    return kothCurrent.status;
  }, [kothCurrent]);

  const endTimestamp = useMemo(() => {
    if (!kothCurrent) return new Date().getTime();
    return kothCurrent.endTimestamp;
  }, [kothCurrent]);

  // Handle ended cooldown: show ended state using server-provided timestamp,
  // then return to waiting once the countdown expires.
  const endedResetAt = useMemo(() => {
    if (status !== "completed" || !kothCurrent?.nextAvailableKoth) return null;
    return kothCurrent.nextAvailableKoth;
  }, [status, kothCurrent?.nextAvailableKoth]);

  const nowSec = Math.floor(getSyncedNow() / 1000);
  const isEndedCooldownActive = endedResetAt ? endedResetAt > nowSec : false;
  const effectiveStatus =
    status === "completed" && !isEndedCooldownActive ? "waiting" : status;

  const { play } = useMusic();

  const setIsGameLive = useSetAtom(isGameLiveAtom);
  const setKothTourMode = useSetAtom(kothTourModeAtom);
  const { startNextStep, closeNextStep } = useNextStep();

  const handleStartKothTour = useCallback(() => {
    try {
      closeNextStep();
      // eslint-disable-next-line no-empty
    } catch {}
    setKothTourMode("koth");
    startNextStep("kothWaitingTour");
  }, [closeNextStep, setKothTourMode, startNextStep]);

  useEffect(() => {
    const isLive = effectiveStatus === "live";
    setIsGameLive(isLive);
    return () => setIsGameLive(false);
  }, [effectiveStatus, setIsGameLive]);

  useEffect(() => {
    if (status === "completed") {
      queryClient.invalidateQueries({ queryKey: ["solana.getUsdcBalance"] });
    }
  }, [status, queryClient]);

  useEffect(() => {
    if (effectiveStatus === "live") {
      play({ ...MATCH_TRACK, autoPlay: true });

      return () => {
        play({ ...MENU_TRACK, autoPlay: true });
      };
    }
  }, [effectiveStatus, play]);

  // Show loading only when we have no data to show AND the initial API request is still in flight.
  // If SSE already sent a snapshot, we have kothCurrent → show UI even while the query finishes.
  const hasReceivedData = hasSnapshotRef.current || data !== undefined;
  const isLoading = isKothQueryPending && !hasReceivedData && !kothCurrent;

  const sseErrorOverlay = (
    <Activity mode={isSseErrorOpen ? "visible" : "hidden"}>
      <div className="fixed inset-0 z-1002! flex items-center justify-center bg-indigo-950/90 backdrop-blur-xs px-6 text-left">
        <div className="relative w-88 sm:w-[24rem] flex flex-col items-center gap-6 text-center overflow-x-visible">
          <div className="relative flex flex-col items-center justify-center w-full h-40 overflow-x-visible">
            <img
              src={"/assets/connection-error.png"}
              alt="Info"
              className="h-40 object-contain"
              style={{ imageRendering: "pixelated" }}
            />
          </div>
          <div className="flex flex-col items-center">
            <div className="text-2xl font-bold z-100 uppercase">
              <AnimatedWaiting
                prefix="Reconnecting"
                className="text-white!"
                style={{ textShadow: generateTextShadow(1.5) }}
              />
            </div>
          </div>
        </div>
      </div>
    </Activity>
  );

  if (isLoading) {
    return (
      <>
        <AnimatedLoading />
        {sseErrorOverlay}
      </>
    );
  }

  // No current KOTH (e.g. after cashout, or between rounds) → show waiting
  if (!kothCurrent || !Object.keys(kothCurrent).length) {
    return (
      <>
        <KOTHWaiting
          kothCurrent={null}
          currentUserAddress={currentUserAddress}
          kothType={kothType}
          rookieBlocked={rookieBlocked}
          onHowItWorkClick={handleStartKothTour}
        />
        {sseErrorOverlay}
      </>
    );
  }

  // Live state
  if (effectiveStatus === "live") {
    return (
      <>
        <KOTHLive kothCurrent={kothCurrent} endTimestamp={endTimestamp} />
        {sseErrorOverlay}
      </>
    );
  }

  // Ended state: show results with temporary countdown back to lobby
  if (status === "completed") {
    return (
      <>
        <KOTHEnded kothCurrent={kothCurrent} endedResetAt={endedResetAt} />
        {sseErrorOverlay}
      </>
    );
  }

  // Waiting state (default)
  return (
    <>
      <KOTHWaiting
        kothCurrent={kothCurrent}
        currentUserAddress={currentUserAddress}
        kothType={kothType}
        rookieBlocked={rookieBlocked}
        onHowItWorkClick={handleStartKothTour}
      />
      {sseErrorOverlay}
    </>
  );
}
